import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import LogDateField from "../../UI/LogDateField";
import LogDatePickerPopover from "../../UI/LogDatePickerPopover";
import LogTimeField from "../../UI/LogTimeField";
import DoseUnitField from "./DoseUnitField";
import {
  MEDICATION_CATEGORY_OPTIONS,
  MEDICATION_FORM_OPTIONS,
  MEDICATION_SCHEDULE_TYPE_OPTIONS,
  MEDICATION_WEEKDAY_OPTIONS,
  createMedicationSchedule,
  formatMedicationProviderLabel,
  normalizeMedicationSchedule,
  resolveMedicationProviderDisplay,
  summarizeMedicationDetail,
} from "./childMedicationHelpers";
import colors from "../../../assets/theme/colors";

const tone = {
  ink: colors.landing.heroText,
  muted: colors.semantic.ink2,
  border: colors.semantic.profileFlow.cardBorder,
  active: "#6EAA9C",
  activeDark: "#2F6F5F",
  activeBg: "#D9EFE8",
  surface: "#FBFDFB",
  soft: "#F2F8F5",
};

const scheduleIconMap = {
  scheduled: ScheduleRoundedIcon,
  every_x_hours: AccessTimeRoundedIcon,
  specific_days: CalendarTodayRoundedIcon,
  as_needed: NotificationsNoneRoundedIcon,
  occasionally: EventRepeatRoundedIcon,
  short_course: CalendarTodayRoundedIcon,
};

const scheduleHelp = {
  scheduled: "Fixed daily times",
  every_x_hours: "Repeat on an interval",
  specific_days: "Only certain weekdays",
  as_needed: "Only when a symptom appears",
  occasionally: "Irregular, no set schedule",
  short_course: "A set number of days",
};

const fieldLabelSx = {
  fontSize: "0.68rem",
  lineHeight: 1.1,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: tone.muted,
};

const RequiredMark = () => (
  <Box component="span" aria-hidden="true" sx={{ color: "#B42318", ml: 0.25 }}>*</Box>
);

const inputSx = {
  "& .MuiInputBase-root": {
    minHeight: 48,
    borderRadius: "12px",
    bgcolor: "rgba(255,255,255,0.96)",
    fontWeight: 700,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: tone.border,
    borderWidth: "1px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#A3AAAA",
    fontWeight: 400,
    opacity: 1,
  },
};

const defaultSchedule = () => createMedicationSchedule();

const ensureSchedules = (entry) => {
  const schedules = Array.isArray(entry?.schedules) && entry.schedules.length
    ? entry.schedules.map(normalizeMedicationSchedule)
    : [defaultSchedule()];

  return schedules.length ? schedules : [defaultSchedule()];
};

const getPrimaryScheduleType = (entry = {}) => {
  const optionValues = MEDICATION_SCHEDULE_TYPE_OPTIONS.map((option) => option.value);
  return optionValues.includes(entry.scheduleType) ? entry.scheduleType : "scheduled";
};

const getCategoryOptions = () => (
  MEDICATION_CATEGORY_OPTIONS.filter((option) => ["prescription", "otc", "supplement"].includes(option.value))
);

const getTodayDateKey = () => new Date().toISOString().slice(0, 10);

const parseDateKey = (value = "") => {
  if (!value) return null;
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateKey = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
};

const MedicationDetailCard = ({
  entry,
  index,
  onChange,
  onRemove,
  onSave,
  title,
  subtitle,
  copy = {},
  providers = [],
  saveStatus = "idle",
  saveError = "",
  saveWarning = "",
}) => {
  const [datePicker, setDatePicker] = useState({ field: null, anchor: null });
  const normalizedSchedules = ensureSchedules(entry);
  const scheduleType = getPrimaryScheduleType(entry);
  const category = entry.category || "prescription";
  const form = entry.form || "pill";
  const selectedDays = Array.isArray(entry.selectedDays) ? entry.selectedDays : [];
  const requiresTime = ["scheduled", "specific_days"].includes(scheduleType);
  const requiresInterval = scheduleType === "every_x_hours";
  const requiresCourse = scheduleType === "short_course";
  const requiresPrnReason = scheduleType === "as_needed";
  const hasCourseLength = Boolean(String(entry.courseEndDate || "").trim() || String(entry.courseDays || "").trim());
  const missingRequiredFields = [
    !String(entry?.name || "").trim() ? "medication name" : "",
    !normalizedSchedules.length || normalizedSchedules.some((schedule) => !String(schedule?.dose || "").trim()) ? "dose" : "",
    requiresTime && normalizedSchedules.some((schedule) => !String(schedule?.time || "").trim()) ? "time" : "",
    requiresInterval && !String(entry.intervalHours || "").trim() ? "repeat interval" : "",
    requiresCourse && !String(entry.courseStartDate || entry.startDate || "").trim() ? "start date" : "",
    requiresCourse && !hasCourseLength ? "course length" : "",
    requiresPrnReason && !String(entry.asNeededReason || entry.purpose || "").trim() ? "when to use it" : "",
  ].filter(Boolean);
  const canSave = missingRequiredFields.length === 0;
  const isSaving = saveStatus === "saving";
  const isSaved = saveStatus === "saved";
  const saveButtonLabel = isSaving
    ? "Saving…"
    : isSaved
      ? "Medication saved"
      : saveStatus === "failed"
        ? "Try again"
        : copy.saveButtonLabel || "Add to master list";
  const summary = summarizeMedicationDetail({
    ...entry,
    schedules: normalizedSchedules,
  });
  const activeProviders = useMemo(() => (
    Array.isArray(providers) ? providers.filter((provider) => provider?.isActive !== false) : []
  ), [providers]);
  const currentProvider = useMemo(() => (
    Array.isArray(providers)
      ? providers.find((provider) => provider?.id === entry.providerId) || null
      : null
  ), [entry.providerId, providers]);
  const resolvedProvider = useMemo(
    () => resolveMedicationProviderDisplay(entry, providers),
    [entry, providers]
  );
  const missingProviderOption = useMemo(() => {
    if (currentProvider || !entry.providerId) {
      return null;
    }

    return {
      id: entry.providerId,
      name: resolvedProvider.label || entry.providerId,
      specialty: "",
      isActive: false,
    };
  }, [currentProvider, entry.providerId, resolvedProvider.label]);
  const providerOptions = useMemo(() => {
    if (missingProviderOption) {
      return [missingProviderOption, ...activeProviders];
    }

    if (currentProvider && currentProvider.isActive === false && !activeProviders.some((provider) => provider.id === currentProvider.id)) {
      return [currentProvider, ...activeProviders];
    }

    return activeProviders;
  }, [activeProviders, currentProvider, missingProviderOption]);

  const updateSchedules = (nextSchedules) => {
    onChange("schedules", nextSchedules.map((schedule, scheduleIndex) => normalizeMedicationSchedule({
      ...schedule,
      id: schedule.id || `${entry.id || index}-${scheduleIndex}`,
    })));
  };

  const updateScheduleField = (scheduleIndex, field, value) => {
    updateSchedules(normalizedSchedules.map((schedule, indexValue) => (
      indexValue === scheduleIndex
        ? { ...schedule, [field]: value }
        : schedule
    )));
  };

  const addScheduleRow = () => {
    updateSchedules([...normalizedSchedules, defaultSchedule()]);
  };

  const removeScheduleRow = (scheduleIndex) => {
    if (normalizedSchedules.length <= 1) return;
    updateSchedules(normalizedSchedules.filter((_, indexValue) => indexValue !== scheduleIndex));
  };

  const updateAllSchedules = (field, value) => {
    updateSchedules(normalizedSchedules.map((schedule) => ({ ...schedule, [field]: value })));
  };

  const toggleDay = (dayValue) => {
    const nextDays = selectedDays.includes(dayValue)
      ? selectedDays.filter((value) => value !== dayValue)
      : [...selectedDays, dayValue];
    onChange("selectedDays", nextDays);
  };

  const handleScheduleTypeChange = (nextType) => {
    onChange("scheduleType", nextType);
  };

  const openDatePicker = (field, event) => {
    setDatePicker({ field, anchor: event.currentTarget });
  };

  const closeDatePicker = () => {
    setDatePicker({ field: null, anchor: null });
  };

  const handleCourseDateChange = (date) => {
    if (!datePicker.field) return;
    onChange(datePicker.field, formatDateKey(date));
    closeDatePicker();
  };

  const renderScheduleDetailFields = () => {
    if (scheduleType === "every_x_hours") {
      return (
        <Box
          sx={{
            border: `1px solid ${tone.active}`,
            borderRadius: "12px",
            bgcolor: "#FCFEFD",
            p: 1.15,
          }}
        >
          <Typography sx={fieldLabelSx}>Repeat details<RequiredMark /></Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.75, mt: 0.7 }}>
            <TextField
              size="small"
              type="number"
              value={entry.intervalHours || ""}
              onChange={(event) => onChange("intervalHours", event.target.value)}
              placeholder="e.g. 6"
              fullWidth
              inputProps={{ min: 1, inputMode: "numeric" }}
              label="Every X hours *"
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
            <LogTimeField
              label="Starting at"
              value={entry.intervalStartTime || ""}
              onChange={(event) => onChange("intervalStartTime", event.target.value)}
              sx={{
                minHeight: 48,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.96)",
                borderColor: tone.border,
              }}
            />
          </Box>
          <Typography sx={{ mt: 0.75, color: tone.muted, fontSize: "0.78rem", lineHeight: 1.35 }}>
            This gives the app an anchor for later “next expected dose” and overdue checks.
          </Typography>
        </Box>
      );
    }

    if (scheduleType === "short_course") {
      return (
        <Box
          sx={{
            border: `1px solid ${tone.active}`,
            borderRadius: "12px",
            bgcolor: "#FCFEFD",
            p: 1.15,
          }}
        >
          <Typography sx={fieldLabelSx}>Course length<RequiredMark /></Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.75, mt: 0.7 }}>
            <LogDateField
              label="Start date *"
              value={parseDateKey(entry.courseStartDate || entry.startDate || getTodayDateKey())}
              onClick={(event) => openDatePicker("courseStartDate", event)}
            />
            <LogDateField
              label="End date"
              value={parseDateKey(entry.courseEndDate || "")}
              placeholder="Select end date"
              onClick={(event) => openDatePicker("courseEndDate", event)}
            />
          </Box>
          <LogDatePickerPopover
            open={Boolean(datePicker.anchor)}
            anchorEl={datePicker.anchor}
            onClose={closeDatePicker}
            value={parseDateKey(datePicker.field === "courseEndDate"
              ? entry.courseEndDate || ""
              : entry.courseStartDate || entry.startDate || getTodayDateKey())}
            onChange={handleCourseDateChange}
          />
          <TextField
            size="small"
            type="number"
            value={entry.courseDays || ""}
            onChange={(event) => onChange("courseDays", event.target.value)}
            placeholder="or enter number of days"
            fullWidth
            inputProps={{ min: 1, inputMode: "numeric" }}
            label="Number of days"
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 0.75, ...inputSx }}
          />
          <Typography sx={{ mt: 0.75, color: tone.muted, fontSize: "0.78rem", lineHeight: 1.35 }}>
            After the end date or day count passes, we can highlight this medication for review instead of auto-removing it.
          </Typography>
        </Box>
      );
    }

    if (scheduleType === "as_needed") {
      return (
        <Box
          sx={{
            border: `1px solid ${tone.active}`,
            borderRadius: "12px",
            bgcolor: "#FCFEFD",
            p: 1.15,
          }}
        >
          <Typography sx={fieldLabelSx}>As-needed instructions</Typography>
          <Stack spacing={0.75} sx={{ mt: 0.7 }}>
            <TextField
              size="small"
              value={entry.asNeededReason || ""}
              onChange={(event) => onChange("asNeededReason", event.target.value)}
              placeholder="e.g. pain, nausea, anxiety, shortness of breath"
              fullWidth
              label="Use when *"
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
            <TextField
              size="small"
              value={entry.maxFrequency || ""}
              onChange={(event) => onChange("maxFrequency", event.target.value)}
              placeholder="e.g. every 4 hours as needed"
              fullWidth
              label="Maximum frequency"
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Stack>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box sx={{ width: "100%", bgcolor: tone.surface }}>
      <Stack spacing={1.8}>
        {saveError ? <Alert severity="error">Failed: {saveError}</Alert> : null}
        {saveWarning ? <Alert severity="warning">{saveWarning}</Alert> : null}
        <Box>
          {title ? (
            <Typography sx={{ fontWeight: 900, color: tone.ink, lineHeight: 1.05, fontSize: "1.2rem" }}>
              {title}
            </Typography>
          ) : null}
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.35 }}>
              {subtitle}
            </Typography>
          ) : null}
          <Typography sx={{ mt: 0.65, color: tone.muted, fontSize: "0.76rem", fontWeight: 700 }}>
            <RequiredMark /> Required
          </Typography>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>{copy.medicationNameLabel || "Medication name"}<RequiredMark /></Typography>
          <TextField
            size="small"
            value={entry.name || ""}
            onChange={(event) => onChange("name", event.target.value)}
            placeholder={copy.medicationNamePlaceholder || "e.g. Guanfacine ER"}
            fullWidth
            sx={{ mt: 0.55, ...inputSx }}
          />
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>
            Provider <Box component="span" sx={{ color: colors.semantic.ink3, letterSpacing: 0, textTransform: "none" }}>optional</Box>
          </Typography>
          <TextField
            select
            size="small"
            value={entry.providerId || ""}
            onChange={(event) => onChange("providerId", event.target.value)}
            fullWidth
            sx={{ mt: 0.55, ...inputSx }}
          >
            <MenuItem value="">No provider</MenuItem>
            {providerOptions.map((provider) => {
              const label = formatMedicationProviderLabel(provider);
              const isCurrentInactiveProvider = provider.id === currentProvider?.id && provider.isActive === false;
              return (
                <MenuItem key={provider.id} value={provider.id} disabled={provider.isActive === false && !isCurrentInactiveProvider}>
                  {label || provider.id}
                  {isCurrentInactiveProvider ? " (inactive)" : ""}
                </MenuItem>
              );
            })}
          </TextField>
          <Typography sx={{ mt: 0.4, color: tone.muted, fontSize: "0.78rem", lineHeight: 1.35 }}>
            {resolvedProvider.label
              ? resolvedProvider.source === "provider"
                ? `Linked provider: ${resolvedProvider.label}`
                : `Fallback text: ${resolvedProvider.label}`
              : "Leave blank to keep this medication unlinked."}
          </Typography>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Dose<RequiredMark /> & form</Typography>
          <Stack spacing={0.75} sx={{ mt: 0.55 }}>
            <DoseUnitField
              dose={normalizedSchedules[0]?.dose || ""}
              unit={normalizedSchedules[0]?.unit || "mg"}
              onDoseChange={(value) => updateAllSchedules("dose", value)}
              onUnitChange={(value) => updateAllSchedules("unit", value)}
              sx={{
                borderColor: tone.border,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.96)",
                "& .MuiInputBase-input": {
                  py: 1.18,
                  fontSize: "0.96rem",
                  fontWeight: 700,
                },
              }}
            />
            <TextField
              select
              size="small"
              value={form}
              onChange={(event) => onChange("form", event.target.value)}
              fullWidth
              sx={inputSx}
            >
              {MEDICATION_FORM_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>How is it taken?</Typography>
          <Box
            sx={{
              mt: 0.75,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 0.75,
            }}
          >
            {MEDICATION_SCHEDULE_TYPE_OPTIONS.map((option) => {
              const isSelected = scheduleType === option.value;
              const OptionIcon = scheduleIconMap[option.value] || ScheduleRoundedIcon;

              return (
                <Button
                  key={option.value}
                  type="button"
                  onClick={() => handleScheduleTypeChange(option.value)}
                  variant="outlined"
                  sx={{
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    minHeight: 74,
                    px: 1.1,
                    py: 0.95,
                    borderRadius: "12px",
                    borderColor: isSelected ? tone.activeDark : tone.border,
                    borderWidth: isSelected ? "1.5px" : "1px",
                    bgcolor: isSelected ? tone.activeBg : "rgba(255,255,255,0.95)",
                    color: tone.ink,
                    textTransform: "none",
                    textAlign: "left",
                    "&:hover": {
                      borderColor: isSelected ? tone.activeDark : tone.active,
                      bgcolor: isSelected ? tone.activeBg : "#F8FCFA",
                    },
                  }}
                >
                  <OptionIcon sx={{ fontSize: 18, color: isSelected ? tone.activeDark : tone.muted, mr: 0.85, mt: 0.1 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: "0.9rem", lineHeight: 1.1 }}>
                      {option.label}
                    </Typography>
                    <Typography sx={{ mt: 0.25, color: tone.muted, fontSize: "0.76rem", lineHeight: 1.25 }}>
                      {scheduleHelp[option.value]}
                    </Typography>
                  </Box>
                </Button>
              );
            })}
          </Box>
        </Box>

        {["scheduled", "specific_days"].includes(scheduleType) ? (
          <Box
            sx={{
              border: `1px solid ${tone.active}`,
              borderRadius: "12px",
              bgcolor: "#FCFEFD",
              p: 1.15,
            }}
          >
            <Typography sx={fieldLabelSx}>Times each day<RequiredMark /></Typography>
            <Stack spacing={0.75} sx={{ mt: 0.7 }}>
              {normalizedSchedules.map((schedule, scheduleIndex) => (
                <Box
                  key={schedule.id || `${entry.id || index}-${scheduleIndex}`}
                  sx={{ display: "flex", alignItems: "stretch", gap: 0.75 }}
                >
                  <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                    <LogTimeField
                      label="Time *"
                      value={schedule.time || ""}
                      onChange={(event) => updateScheduleField(scheduleIndex, "time", event.target.value)}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removeScheduleRow(scheduleIndex)}
                    aria-label="Remove schedule row"
                    disabled={normalizedSchedules.length <= 1}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      bgcolor: "#F7FAF8",
                      color: colors.semantic.ink3,
                      border: `1px solid ${tone.border}`,
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            <Button
              type="button"
              onClick={addScheduleRow}
              startIcon={<AddRoundedIcon />}
              variant="text"
              sx={{
                mt: 0.55,
                px: 0.2,
                textTransform: "none",
                fontWeight: 800,
                color: tone.active,
                "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
              }}
            >
              {copy.addScheduleLabel || "Add another time"}
            </Button>

            {scheduleType === "specific_days" ? (
              <Box sx={{ mt: 1.1 }}>
                <Typography sx={fieldLabelSx}>Which days</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 0.55, mt: 0.65 }}>
                  {MEDICATION_WEEKDAY_OPTIONS.map((day) => {
                    const isSelected = selectedDays.includes(day.value);
                    return (
                      <Button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        variant="outlined"
                        sx={{
                          minWidth: 0,
                          height: 48,
                          borderRadius: "10px",
                          borderColor: isSelected ? tone.active : tone.border,
                          bgcolor: isSelected ? "#E7F4EF" : "rgba(255,255,255,0.94)",
                          color: isSelected ? tone.activeDark : tone.muted,
                          fontWeight: 900,
                        }}
                      >
                        {day.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : null}

        {renderScheduleDetailFields()}

        <Box>
          <Typography sx={fieldLabelSx}>
            What it's for <Box component="span" sx={{ color: colors.semantic.ink3, letterSpacing: 0, textTransform: "none" }}>optional</Box>
          </Typography>
          <TextField
            size="small"
            value={entry.purpose || ""}
            onChange={(event) => onChange("purpose", event.target.value)}
            placeholder="e.g. focus, ear infection, sleep"
            fullWidth
            sx={{ mt: 0.55, ...inputSx }}
          />
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>
            Notes for the care circle <Box component="span" sx={{ color: colors.semantic.ink3, letterSpacing: 0, textTransform: "none" }}>optional</Box>
          </Typography>
          <TextField
            multiline
            minRows={2}
            value={entry.notes || ""}
            onChange={(event) => onChange("notes", event.target.value)}
            placeholder="e.g. give with food · no issues so far · finish the full course"
            fullWidth
            sx={{
              mt: 0.55,
              ...inputSx,
              "& .MuiInputBase-root": {
                ...inputSx["& .MuiInputBase-root"],
                alignItems: "flex-start",
                py: 0.35,
              },
            }}
          />
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Category</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0.65, mt: 0.65 }}>
            {getCategoryOptions().map((option) => {
              const isSelected = category === option.value;
              return (
                <Button
                  key={option.value}
                  type="button"
                  onClick={() => onChange("category", option.value)}
                  variant="outlined"
                  sx={{
                    minHeight: 48,
                    borderRadius: "10px",
                    borderColor: isSelected ? tone.active : tone.border,
                    bgcolor: isSelected ? "#E7F4EF" : "rgba(255,255,255,0.95)",
                    color: isSelected ? tone.activeDark : tone.muted,
                    textTransform: "none",
                    fontWeight: 900,
                    "&:hover": {
                      borderColor: tone.active,
                      bgcolor: isSelected ? "#E7F4EF" : "#F8FCFA",
                    },
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            borderRadius: "12px",
            bgcolor: tone.soft,
            color: tone.muted,
            px: 1.05,
            py: 0.9,
          }}
        >
          <CheckRoundedIcon sx={{ color: canSave ? tone.active : colors.semantic.ink3, fontSize: 20, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.82rem", lineHeight: 1.35 }}>
            {summary || copy.summaryFallback || "Fill in a name to preview the summary your care circle will see."}
          </Typography>
        </Box>

        {!canSave ? (
          <Typography
            role="status"
            sx={{ color: tone.muted, fontSize: "0.8rem", fontWeight: 750, lineHeight: 1.35 }}
          >
            Complete required fields: {missingRequiredFields.join(", ")}
          </Typography>
        ) : null}

        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(100px, 0.34fr) minmax(0, 1fr)", gap: 0.75 }}>
          <Button
            type="button"
            onClick={onRemove}
            disabled={isSaving || isSaved}
            variant="contained"
            sx={{
              minHeight: 54,
              borderRadius: "12px",
              bgcolor: "#EEF3F1",
              color: tone.muted,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 900,
              "&:hover": { bgcolor: "#E6EEEB", boxShadow: "none" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={!canSave || isSaving || isSaved}
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <CheckRoundedIcon />}
            variant="contained"
            sx={{
              minHeight: 54,
              borderRadius: "12px",
              bgcolor: canSave ? tone.active : "#EAF0ED",
              color: canSave ? "#fff" : tone.muted,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 900,
              "&:hover": canSave ? { bgcolor: tone.activeDark, boxShadow: "none" } : undefined,
            }}
          >
            {saveButtonLabel}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default MedicationDetailCard;
